import { formatAddress, isResource } from '@medplum/core'
import type {
  Address,
  CodeableConcept,
  Coding,
  ContactPoint,
  Identifier,
  Organization,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import {
  codeFromCodeableConcept,
  systemFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  FAX_DIRECTION_EXTENSION,
  FaxDirection,
  identifierTypeAccountNumber,
  LANGUAGE_SYSTEM,
  NON_NETWORK_TAG_CODE,
  NPI_SYSTEM,
  ORGANIZATION_AVAILABILITY_SYSTEM,
  ORGANIZATION_CONTRACT_SYSTEM,
  ORGANIZATION_DESIGNATION_SYSTEM,
  ORGANIZATION_SERVICE_CATEGORY_SYSTEM,
  ORGANIZATION_SYSTEM,
  ORGANIZATION_TYPE,
  PROVIDER_NETWORK_STATUS_DISPLAY,
  PROVIDER_NETWORK_STATUS_SYSTEM,
  ProviderNetworkStatus,
  QUEST_ACCOUNT_NUMBER_IDENTIFIER_SYSTEM,
  SEEN_HEALTH_ORG_IDENTIFIER,
  TAX_ID_SYSTEM,
} from '#src/codeSystems.ts'

import { Extension, Tag } from '#src/decorators.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'

import { invariant } from '#src/invariant.ts'
import * as contactInfo from '#src/models/contactInfo.ts'
import {
  ORGANIZATION_TYPES,
  OrganizationType,
} from '#src/models/organizations/constants.ts'
import {
  getFaxNumbers,
  getPrimaryEmail,
  getPrimaryFaxNumber,
  getPrimaryPhoneNumber,
  homeAddressOrFallback,
} from '#src/models/patients/patients.ts'
import {
  type NetworkContract,
  ServiceCategory,
  type ServiceCategoryCode,
} from '#src/models/practitioner.ts'
import {
  getLanguageDisplay,
  type ProfileLanguageCode,
} from '#src/models/profile.ts'
import type { QueryTypes } from '#src/search.ts'
import {
  ORGANIZATION_SCHEDULING_RULES_CONFIG_SYSTEM,
  ORGANIZATION_SCHEDULING_RULES_TEXT_SYSTEM,
} from '#src/structureDefinitions.ts'
import { isOfType } from '#src/utils.ts'

export const Designation = {
  facility: {
    system: ORGANIZATION_DESIGNATION_SYSTEM,
    code: 'facility',
    display: 'Facility',
  },
  provider: {
    system: ORGANIZATION_DESIGNATION_SYSTEM,
    code: 'provider',
    display: 'Provider',
  },
  'group-practice': {
    system: ORGANIZATION_DESIGNATION_SYSTEM,
    code: 'group-practice',
    display: 'Group Practice',
  },
  'solo-practitioner': {
    system: ORGANIZATION_DESIGNATION_SYSTEM,
    code: 'solo-practitioner',
    display: 'Solo Practitioner',
  },
  pharmacy: {
    system: ORGANIZATION_DESIGNATION_SYSTEM,
    code: 'pharmacy',
    display: 'Pharmacy',
  },
  vendor: {
    system: ORGANIZATION_DESIGNATION_SYSTEM,
    code: 'vendor',
    display: 'Vendor',
  },
} as const satisfies Record<string, Coding>

export type DesignationCode = keyof typeof Designation

// Deterministic identifier value for a private-practice shell org: the
// practitioner's own (type-1) NPI, prefixed so it can never be confused with
// an organization's billing NPI or another ORGANIZATION_SYSTEM identifier.
function privatePracticeIdentifierValue(practitionerNpi: string): string {
  return `private-practice-${practitionerNpi}`
}

// 'available' means trusted enough to receive referrals; 'preferred' is the top choice.
export const OrganizationAvailabilities = {
  preferred: {
    system: ORGANIZATION_AVAILABILITY_SYSTEM,
    code: 'preferred',
    display: 'Preferred',
  },
  available: {
    system: ORGANIZATION_AVAILABILITY_SYSTEM,
    code: 'available',
    display: 'Available',
  },
} as const satisfies Record<string, Coding>

export type OrganizationAvailability = keyof typeof OrganizationAvailabilities

export class ExtendedOrganization extends ExtendedResource<Organization> {
  static readonly RESOURCE_TYPE = 'Organization'

  // Version-history parsing keeps only entries the class claims
  // (parseResourceVersion); the base returns false, which silently empties
  // the history dialog for any model that forgets this override.
  static isInstance(value: unknown): value is Organization {
    return isResource(value) && isOfType(value, 'Organization')
  }

  constructor({
    type,
    ...rest
  }: Omit<Organization, 'resourceType' | 'type'> & {
    type: OrganizationType
  }) {
    super({ active: true, ...rest })
    this.type = type
  }

  static querySeenHealthOrg(): QueryTypes {
    return ExtendedOrganization.byIdentifier(
      ORGANIZATION_SYSTEM,
      SEEN_HEALTH_ORG_IDENTIFIER
    )
  }

  static byName(name: string): QueryTypes {
    return {
      // Strip commas, as the server will treat them as OR conditions
      name: name.replace(/,/g, ''),
    }
  }

  // Builds an external-provider Organization keyed by its billing NPI and/or
  // TIN. At least one of npi/tin is required so the org can be deduped.
  static build(params: {
    name?: string
    // Alternate/trade names (e.g. an NPPES "Doing Business As" name). Stored in
    // FHIR Organization.alias, which the `name` search param also matches.
    alias?: string[]
    npi?: string
    tin?: string
    address?: Address[]
    telecom?: ContactPoint[]
    type?: OrganizationType
  }): ExtendedOrganization {
    invariant(
      params.npi || params.tin,
      'An Organization requires a billing NPI or TIN to be built'
    )
    const identifier = compact<Identifier>([
      params.npi ? { system: NPI_SYSTEM, value: params.npi } : undefined,
      params.tin ? { system: TAX_ID_SYSTEM, value: params.tin } : undefined,
    ])
    return new ExtendedOrganization({
      type: params.type ?? OrganizationType.EXTERNAL_PROVIDER,
      name: params.name,
      alias: params.alias?.length ? params.alias : undefined,
      identifier,
      address: params.address,
      telecom: params.telecom,
    })
  }

  // Builds a shell Organization for a private-practice / sole-proprietor
  // practitioner who has no billable group to attach to. The org has no NPI/TIN
  // of its own, so it bypasses build()'s dedupe invariant; instead, when the
  // practitioner's individual (type-1) NPI is known it becomes a deterministic
  // shell identifier (see byPrivatePracticeNpi) so repeat submissions upsert
  // the same shell. The solo-practitioner designation tag marks it as a shell
  // for later dedupe/merge tooling.
  static buildPrivatePractice(params: {
    name: string
    practitionerNpi?: string
    address?: Address[]
    telecom?: ContactPoint[]
  }): ExtendedOrganization {
    const organization = new ExtendedOrganization({
      type: OrganizationType.EXTERNAL_PROVIDER,
      name: params.name,
      identifier: params.practitionerNpi
        ? [
            {
              system: ORGANIZATION_SYSTEM,
              value: privatePracticeIdentifierValue(params.practitionerNpi),
            },
          ]
        : undefined,
      address: params.address,
      telecom: params.telecom,
    })
    organization.designation = 'solo-practitioner'
    return organization
  }

  // Idempotent lookup for a practitioner's private-practice shell org, keyed
  // by their individual NPI. The prefix keeps the value distinct from the
  // org-level identifiers sharing ORGANIZATION_SYSTEM (e.g. seen-health).
  static byPrivatePracticeNpi(practitionerNpi: string): QueryTypes {
    return ExtendedOrganization.byIdentifier(
      ORGANIZATION_SYSTEM,
      privatePracticeIdentifierValue(practitionerNpi)
    )
  }

  // Idempotent lookup preferring billing NPI, falling back to TIN. The two
  // identifier tokens are comma-joined so Medplum OR-matches them.
  static byNpiOrTin(params: { npi?: string; tin?: string }): QueryTypes {
    invariant(
      params.npi || params.tin,
      'byNpiOrTin requires a billing NPI or TIN'
    )
    const tokens = compact([
      params.npi ? `${NPI_SYSTEM}|${params.npi}` : undefined,
      params.tin ? `${TAX_ID_SYSTEM}|${params.tin}` : undefined,
    ])
    return { identifier: tokens.join(',') }
  }

  get questAccountNumber(): string | undefined {
    return (
      this.identifier(QUEST_ACCOUNT_NUMBER_IDENTIFIER_SYSTEM)?.value ??
      this.identifierByType(identifierTypeAccountNumber)?.value
    )
  }

  get name(): string | undefined {
    return this.resource.name
  }

  set name(value: string | undefined) {
    this.resource.name = value
  }

  get active(): boolean {
    return !!this.resource.active
  }

  set active(value: boolean) {
    this.resource.active = value
  }

  get typeCodeableConcept(): CodeableConcept | undefined {
    return (this.resource.type ?? []).find(
      (codeableConcept) =>
        systemFromCodeableConcept(codeableConcept) === ORGANIZATION_TYPE
    )
  }
  get type(): OrganizationType | undefined {
    const type = this.typeCodeableConcept
    return type && (codeFromCodeableConcept(type) as OrganizationType)
  }

  set type(value: OrganizationType) {
    this.resource.type ??= []
    const typeIndex = this.resource.type.findIndex(
      (codeableConcept) =>
        systemFromCodeableConcept(codeableConcept) === ORGANIZATION_TYPE
    )
    if (typeIndex === -1) {
      this.resource.type?.push(ORGANIZATION_TYPES[value])
    } else {
      this.resource.type[typeIndex] = ORGANIZATION_TYPES[value]
    }
  }

  @Tag({ system: ORGANIZATION_DESIGNATION_SYSTEM })
  _designation?: Coding

  get designation(): DesignationCode | undefined {
    return this._designation?.code as DesignationCode | undefined
  }

  set designation(code: DesignationCode | undefined) {
    this._designation = code ? Designation[code] : undefined
  }

  @Tag({ system: ORGANIZATION_SERVICE_CATEGORY_SYSTEM })
  _serviceCategory?: Coding

  get serviceCategory(): ServiceCategoryCode | undefined {
    return this._serviceCategory?.code as ServiceCategoryCode | undefined
  }

  set serviceCategory(code: ServiceCategoryCode | undefined) {
    this._serviceCategory = code
      ? {
          system: ORGANIZATION_SERVICE_CATEGORY_SYSTEM,
          ...ServiceCategory[code],
        }
      : undefined
  }

  @Tag({ system: ORGANIZATION_CONTRACT_SYSTEM })
  _contractType?: Coding

  // Undefined means no contract is recorded, which is distinct from a
  // deliberate Direct. Only the exact codes count: an unrecognized code, or
  // one carried under another system, reads as unrecorded.
  get networkContract(): NetworkContract | undefined {
    const code = this._contractType?.code
    if (code === 'astrana') {
      return 'Astrana'
    }
    if (code === 'direct') {
      return 'Direct'
    }
    return undefined
  }

  set networkContract(value: NetworkContract) {
    this._contractType = {
      system: ORGANIZATION_CONTRACT_SYSTEM,
      code: value.toLowerCase(),
      display: value,
    }
  }

  @Tag({ system: PROVIDER_NETWORK_STATUS_SYSTEM })
  _networkStatus?: Coding

  // Derived clinician-visibility status. An explicit status tag wins; otherwise
  // a contract tag (astrana or direct) counts as in-network; otherwise
  // non-network. Only the explicit non-network tag is written today; the rest
  // is derived until the backfill stamps internal/in-network.
  get networkStatus(): ProviderNetworkStatus {
    const code = this._networkStatus?.code
    if (
      code === ProviderNetworkStatus.Internal ||
      code === ProviderNetworkStatus.InNetwork ||
      code === ProviderNetworkStatus.NonNetwork
    ) {
      return code
    }
    const contract = this._contractType?.code
    if (contract === 'astrana' || contract === 'direct') {
      return ProviderNetworkStatus.InNetwork
    }
    return ProviderNetworkStatus.NonNetwork
  }

  set networkStatus(status: ProviderNetworkStatus | undefined) {
    this._networkStatus = status
      ? {
          system: PROVIDER_NETWORK_STATUS_SYSTEM,
          code: status,
          display: PROVIDER_NETWORK_STATUS_DISPLAY[status],
        }
      : undefined
  }

  get isNonNetwork(): boolean {
    return this._networkStatus?.code === NON_NETWORK_TAG_CODE
  }

  @Tag({ system: ORGANIZATION_AVAILABILITY_SYSTEM })
  _availability?: Coding

  get availability(): OrganizationAvailability | undefined {
    return this._availability?.code as OrganizationAvailability | undefined
  }

  set availability(value: OrganizationAvailability | undefined) {
    this._availability = value ? OrganizationAvailabilities[value] : undefined
  }

  @Extension({
    url: ORGANIZATION_SCHEDULING_RULES_TEXT_SYSTEM,
    kind: 'valueString',
  })
  private _schedulingRulesText?: string

  // The ops scheduling-rules document verbatim. Always write together with
  // schedulingRulesConfigJson in ONE update so each Organization version
  // carries a matched text+config pair.
  get schedulingRulesText(): string | undefined {
    return this._schedulingRulesText
  }

  set schedulingRulesText(value: string | undefined) {
    this._schedulingRulesText = value
  }

  @Extension({
    url: ORGANIZATION_SCHEDULING_RULES_CONFIG_SYSTEM,
    kind: 'valueString',
  })
  private _schedulingRulesConfigJson?: string

  // Compiled RulesConfig JSON, validated at save time. Parse with
  // parseRulesConfigJson from scheduling/config.ts — kept off this
  // model so the engine (and ical.js) stays out of the base import graph.
  get schedulingRulesConfigJson(): string | undefined {
    return this._schedulingRulesConfigJson
  }

  set schedulingRulesConfigJson(value: string | undefined) {
    this._schedulingRulesConfigJson = value
  }

  /** Search-rank weight: preferred outranks available outranks unset. */
  get availabilityRank(): number {
    switch (this.availability) {
      case 'preferred':
        return 2
      case 'available':
        return 1
      default:
        return 0
    }
  }

  @Tag({ system: LANGUAGE_SYSTEM, allowMultiple: true })
  _languages?: Coding[]

  get languageCodes(): ProfileLanguageCode[] {
    return (this._languages ?? []).map((c) => ({
      code: c.code ?? '',
      display: c.display ?? getLanguageDisplay(c.code ?? ''),
      preferred: false,
    }))
  }

  set languageCodes(value: ProfileLanguageCode[]) {
    this._languages = value.map((language) => ({
      system: LANGUAGE_SYSTEM,
      code: language.code,
      display: language.display ?? getLanguageDisplay(language.code),
    }))
  }

  get npi(): string | undefined {
    return this.identifier(NPI_SYSTEM)?.value
  }

  set npi(value: string | undefined) {
    if (value) {
      this.setIdentifier(NPI_SYSTEM, value)
    } else {
      this.removeIdentifier(NPI_SYSTEM)
    }
  }

  get taxId(): string | undefined {
    return this.identifier(TAX_ID_SYSTEM)?.value
  }

  set taxId(value: string | undefined) {
    if (value) {
      this.setIdentifier(TAX_ID_SYSTEM, value)
    } else {
      this.removeIdentifier(TAX_ID_SYSTEM)
    }
  }

  // Non-excluding variant for the fax recipient list and admin provider
  // catalog, where non-network organizations must stay visible.
  static faxRecipientSearchQuery(): QueryTypes {
    return {
      _count: 1000,
      _sort: '-_lastUpdated',
    }
  }

  static searchQuery(): QueryTypes {
    return {
      ...ExtendedOrganization.faxRecipientSearchQuery(),
      '_tag:not': `${PROVIDER_NETWORK_STATUS_SYSTEM}|${NON_NETWORK_TAG_CODE}`,
    }
  }

  get primaryAddress(): Address | undefined {
    return homeAddressOrFallback(this.resource)
  }

  get contactMethods(): ContactPoint[] {
    return contactInfo.getContactMethods(this.resource)
  }

  set contactMethods(methods: ContactPoint[]) {
    contactInfo.setContactMethods(this.resource, methods)
  }

  get addresses(): Address[] {
    return this.resource.address ?? []
  }

  set addresses(addresses: Address[]) {
    // Empty FHIR arrays fail validation; clear the field instead.
    this.resource.address = addresses.length > 0 ? addresses : undefined
  }

  get primaryPhoneNumber(): string | undefined {
    return getPrimaryPhoneNumber(this.resource)
  }

  get primaryEmail(): string | undefined {
    return getPrimaryEmail(this.resource)
  }

  get primaryFaxNumber(): string | undefined {
    return getPrimaryFaxNumber(this.resource)
  }

  // All fax numbers on file, not just primaryFaxNumber — used by the Send Fax
  // recipient picker, which shows one row per (organization, fax number) pair.
  faxNumbers(): string[] {
    return getFaxNumbers(this.resource)
  }

  /*
   * Fax number shown on outgoing documents (e.g. order PDF headers).
   * Only meaningful for the Seen Health org, which tags its telecom entries
   * with a fax-direction extension. Falls back to primaryFaxNumber.
   */
  get inboundFaxNumber(): string | undefined {
    return this.getFaxNumberByDirection(FaxDirection.INBOUND)
  }

  /*
   * Fax number used as the sender when transmitting outbound faxes.
   * Only meaningful for the Seen Health org. Falls back to primaryFaxNumber.
   */
  get outboundFaxNumber(): string | undefined {
    return this.getFaxNumberByDirection(FaxDirection.OUTBOUND)
  }

  private getFaxNumberByDirection(direction: FaxDirection): string | undefined {
    const match = (this.resource.telecom ?? [])
      .filter((t) => t.system === 'fax' && t.value !== undefined)
      .find((t) =>
        t.extension?.some(
          (ext) =>
            ext.url === FAX_DIRECTION_EXTENSION && ext.valueCode === direction
        )
      )
    return match?.value ?? this.primaryFaxNumber
  }

  get searchKeywords(): string[] {
    return compact([
      this.name,
      this.id,
      this.primaryAddress && formatAddress(this.primaryAddress),
      this.primaryPhoneNumber,
      this.primaryEmail,
      this.primaryFaxNumber,
    ])
  }
}
